import { useMemo, useState } from 'react';
import {
  Table,
  Avatar,
  Badge,
  Group,
  Text,
  Button,
  Alert,
  Stack,
  UnstyledButton,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import {
  Eye,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Check,
  Link as LinkIcon,
  RefreshCw,
  X,
} from 'react-feather';
import type { CoachCompensationResult } from '../../../../types';
import type { CoachVendorStatus } from '../../../../services/api/billcom';
import { getAvatarColor, getInitialsFromParts } from '../../../../utils/avatarHelpers';
import { formatCents, formatCentsCompact } from '../../../../utils/formatters';
import {
  COMPENSATION_STATUS_CONFIG,
  getCompensationDisplayStatus,
  WRAPPING_BADGE_STYLES,
} from '../../../../config/compensationConfig';

type SortColumn = 'name' | 'groups' | 'monthly' | 'total' | 'status';
type SortDirection = 'asc' | 'desc';

/**
 * MED-677: Binary coach lifecycle status shown next to the coach name in the
 * unified "Coaches" list. A coach is "Confirmed" once their contract_status
 * has reached `announced` or `signed`; otherwise they are "Potential" — still
 * in the pool but not yet locked in.
 */
function getCoachLifecycleBadge(coach: CoachCompensationResult): {
  label: string;
  color: string;
} {
  if (coach.contract_status === 'announced' || coach.contract_status === 'signed') {
    return { label: 'Confirmed', color: 'medleyGreen' };
  }
  return { label: 'Potential', color: 'gray' };
}

function isDeclined(coach: CoachCompensationResult): boolean {
  return coach.availability_status === 'unavailable';
}

interface CoachCompensationTableProps {
  coaches: CoachCompensationResult[];
  pendingCoaches: CoachCompensationResult[];
  hasRecalculationReset?: boolean;
  onViewCoach: (coachUserId: string) => void;
  vendorStatus?: Map<string, CoachVendorStatus>;
  onLinkVendor?: (coachUserId: string, coachName: string, coachEmail: string) => void;
  // MED-1178: unlink a coach's Bill.com vendor. `hasPayments` drives the
  // confirmation warning (payments were created against the current vendor).
  onUnlinkVendor?: (
    coachUserId: string,
    coachName: string,
    vendorName: string,
    hasPayments: boolean,
  ) => void;
}

export function CoachCompensationTable({
  coaches,
  pendingCoaches,
  hasRecalculationReset,
  onViewCoach,
  vendorStatus,
  onLinkVendor,
  onUnlinkVendor,
}: CoachCompensationTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('total');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // MED-677: Merge the previously split "Potential Coaches" and "Assigned
  // Coaches" sections into a single unified "Coaches" list. Confirmed coaches
  // were previously mis-labeled under the "Potential Coaches" header even when
  // their contract had been announced/signed — now the single list shows a
  // per-row Confirmed/Potential badge derived from contract_status.
  const allCoaches = useMemo(() => [...coaches, ...pendingCoaches], [coaches, pendingCoaches]);

  const sortCoaches = (list: CoachCompensationResult[]) => {
    const sorted = [...list];
    const dir = sortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      switch (sortColumn) {
        case 'name':
          return dir * (a.coach_name ?? '').localeCompare(b.coach_name ?? '');
        case 'groups':
          return dir * (a.group_count - b.group_count);
        case 'monthly':
          return dir * ((a.flat_rate_monthly_cents ?? 0) - (b.flat_rate_monthly_cents ?? 0));
        case 'total':
          return dir * ((a.estimated_total_cents ?? 0) - (b.estimated_total_cents ?? 0));
        case 'status':
          return dir * a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
    return sorted;
  };

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection('desc');
    }
  };

  if (allCoaches.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl" size="sm">
        No compensation calculations available yet.
      </Text>
    );
  }

  const SortHeader = ({
    col,
    label,
    align = 'left',
  }: {
    col: SortColumn;
    label: string;
    align?: 'left' | 'right' | 'center';
  }) => (
    <UnstyledButton onClick={() => handleSort(col)} style={{ width: '100%' }}>
      <Group
        gap={4}
        justify={align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'}
        wrap="nowrap"
      >
        <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>
          {label}
        </Text>
        {sortColumn === col &&
          (sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </Group>
    </UnstyledButton>
  );

  const renderCoachRow = (coach: CoachCompensationResult) => {
    const nameParts = (coach.coach_name ?? '').split(' ');
    const initials = getInitialsFromParts(nameParts[0] ?? '', nameParts.slice(1).join(' ') ?? '');
    const avatarColor = getAvatarColor(coach.coach_user_id);
    const isPending = coach.status === 'pending';
    const hasAdjustments = coach.line_items?.some(li => li.adjusted_total_cents !== null);
    const displayStatus = getCompensationDisplayStatus(coach.status, !!hasAdjustments);
    const statusConfig =
      COMPENSATION_STATUS_CONFIG[displayStatus] ?? COMPENSATION_STATUS_CONFIG.pending;
    const monthlyTotal = (coach.flat_rate_monthly_cents ?? 0) * (coach.program_months ?? 0);
    const totalCents = coach.estimated_total_cents ?? 0;
    const attendanceDepCents = (coach.line_items ?? []).reduce((sum, li) => {
      if (li.payment_track !== 'attendance_dependent_net_10') return sum;
      return sum + (li.adjusted_total_cents ?? li.total_cents);
    }, 0);
    const declined = isDeclined(coach);
    // MED-677: Confirmed vs Potential badge derived from contract_status
    const lifecycleBadge = getCoachLifecycleBadge(coach);
    // MED-677: "Estimated (1 group)" context survives on the total cell for
    // rows that are still pre-assignment so CMs can tell apart real totals
    // from planning estimates even inside the unified list.
    const isPreAssignment = coach.estimation_basis === 'pre_assignment';

    return (
      <Table.Tr
        key={coach.id}
        style={declined ? { opacity: 0.5 } : undefined}
      >
        <Table.Td>
          <Group gap="sm">
            <Avatar size="md" radius="xl" color={avatarColor}>
              {initials}
            </Avatar>
            <div>
              <Text size="sm" fw={600}>
                {coach.coach_name ?? 'Unknown'}
              </Text>
              <Text size="xs" c="dimmed">
                {coach.member_count} participant{coach.member_count !== 1 ? 's' : ''}
              </Text>
            </div>
          </Group>
        </Table.Td>
        {/* MED-677: Lifecycle status column — Confirmed vs Potential */}
        <Table.Td style={{ textAlign: 'center' }}>
          {/* MED-1383: badges wrap instead of clipping in a narrow column. */}
          <Badge
            size="sm"
            variant="light"
            color={lifecycleBadge.color}
            styles={WRAPPING_BADGE_STYLES}
          >
            {lifecycleBadge.label}
          </Badge>
        </Table.Td>
        <Table.Td style={{ textAlign: 'center' }}>
          <Text size="sm" fw={500}>
            {coach.group_count}
          </Text>
        </Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>
          {isPending || !coach.flat_rate_monthly_cents ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Stack gap={0} align="flex-end">
              <Text size="sm" fw={600}>
                {formatCents(coach.flat_rate_monthly_cents)}/mo
              </Text>
              <Text size="xs" c="dimmed">
                {formatCents(monthlyTotal)} total
              </Text>
            </Stack>
          )}
        </Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>
          {isPending ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Text size="sm" fw={600} c="warningAmber.8">
              {formatCents(attendanceDepCents)}
            </Text>
          )}
        </Table.Td>
        <Table.Td style={{ textAlign: 'right' }}>
          {isPending ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Text size="sm" fw={700}>
                {formatCentsCompact(totalCents)}
              </Text>
              {/* MED-677: The "Estimated (1 group)" hint survives on rows that
                  are still pre-assignment so CMs can distinguish planning
                  estimates from real assignment-derived totals. */}
              {isPreAssignment && (
                <Badge size="xs" variant="light" color="blue" styles={WRAPPING_BADGE_STYLES}>
                  Estimated (1 group)
                </Badge>
              )}
            </Group>
          )}
        </Table.Td>
        <Table.Td style={{ textAlign: 'center' }}>
          {(() => {
            const vs = vendorStatus?.get(coach.coach_user_id);
            if (vs?.billcom_vendor_id) {
              // MED-1178: keep the "Mapped" badge but add inline actions so a
              // mis-linked vendor can be corrected. Change = re-open the link
              // modal (link POST overwrites); Unlink = clear the mapping.
              return (
                <Group gap={2} justify="center" wrap="nowrap">
                  <Tooltip
                    label={`Linked to ${vs.billcom_vendor_name} (${vs.billcom_vendor_id})`}
                  >
                    <Badge size="sm" color="medleyGreen" variant="light">
                      Mapped
                    </Badge>
                  </Tooltip>
                  <Tooltip label="Change vendor">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Change vendor"
                      onClick={() =>
                        onLinkVendor?.(
                          coach.coach_user_id,
                          coach.coach_name || 'Coach',
                          coach.coach_email || '',
                        )
                      }
                    >
                      <RefreshCw size={13} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Unlink vendor">
                    <ActionIcon
                      variant="subtle"
                      color="dangerRed"
                      size="sm"
                      aria-label="Unlink vendor"
                      onClick={() =>
                        onUnlinkVendor?.(
                          coach.coach_user_id,
                          coach.coach_name || 'Coach',
                          vs.billcom_vendor_name || '',
                          Boolean(vs.has_scheduled_payments),
                        )
                      }
                    >
                      <X size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            }
            return (
              <Button
                variant="outline"
                color="warningAmber"
                size="xs"
                leftSection={<LinkIcon size={12} />}
                onClick={() =>
                  onLinkVendor?.(
                    coach.coach_user_id,
                    coach.coach_name || 'Coach',
                    coach.coach_email || '',
                  )
                }
              >
                Link Vendor
              </Button>
            );
          })()}
        </Table.Td>
        <Table.Td style={{ textAlign: 'center' }}>
          <Badge
            size="sm"
            color={statusConfig.color}
            variant={statusConfig.variant ?? 'light'}
            leftSection={displayStatus === 'confirmed' ? <Check size={10} /> : undefined}
            styles={WRAPPING_BADGE_STYLES}
          >
            {statusConfig.label}
          </Badge>
        </Table.Td>
        <Table.Td style={{ textAlign: 'center' }}>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<Eye size={14} />}
            onClick={() => onViewCoach(coach.coach_user_id)}
          >
            View / Edit
          </Button>
        </Table.Td>
      </Table.Tr>
    );
  };

  const tableHead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>
          <SortHeader col="name" label="Coach" />
        </Table.Th>
        {/* MED-677: New lifecycle status column (Confirmed vs Potential) sits
            directly to the right of the Coach name column per the ticket. */}
        <Table.Th style={{ textAlign: 'center', width: 110 }}>
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>
            Stage
          </Text>
        </Table.Th>
        <Table.Th style={{ textAlign: 'center', width: 80 }}>
          <SortHeader col="groups" label="Groups" align="center" />
        </Table.Th>
        <Table.Th style={{ textAlign: 'right' }}>
          <SortHeader col="monthly" label="Monthly" align="right" />
        </Table.Th>
        <Table.Th style={{ textAlign: 'right' }}>
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }} ta="right">
            Attendance Dep.
          </Text>
        </Table.Th>
        <Table.Th style={{ textAlign: 'right', minWidth: 200 }}>
          <SortHeader col="total" label="Est. Total" align="right" />
        </Table.Th>
        <Table.Th style={{ textAlign: 'center', width: 120 }}>
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>
            Bill.com
          </Text>
        </Table.Th>
        <Table.Th style={{ textAlign: 'center', width: 140 }}>
          <SortHeader col="status" label="Calc Status" align="center" />
        </Table.Th>
        <Table.Th style={{ textAlign: 'center', width: 80 }}>
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>
            Action
          </Text>
        </Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const hasCoaches = allCoaches.length > 0;

  return (
    <Stack gap="md">
      {hasRecalculationReset && (
        <Alert icon={<AlertTriangle size={16} />} color="orange" variant="light">
          Some confirmed records were reset due to recalculation. Please review and re-confirm.
        </Alert>
      )}

      {/* MED-677: Unified "Coaches" section. Previously split into "Potential
          Coaches" and "Assigned Coaches (To Confirm)" — the split was based on
          estimation_basis, which did not track contract_status, so confirmed
          coaches could still appear under the "Potential" header. The single
          list now relies on the per-row Stage column for that distinction. */}
      {hasCoaches ? (
        <Stack gap={0}>
          <Group
            px="md"
            py="sm"
            gap="sm"
            style={{
              border: '1px solid #E8E6E1',
              borderRadius: '8px 8px 0 0',
              background: 'white',
              borderBottom: 'none',
            }}
          >
            <Text fw={600} size="sm">
              Coaches
            </Text>
            <Badge size="sm" variant="light" color="blue">
              {allCoaches.length}
            </Badge>
          </Group>
          {/* MED-1383: the table is about 1000 px wide. On a narrow screen it scrolls
              inside its card, so the page itself does not scroll sideways. */}
          <Table.ScrollContainer minWidth={1000}>
            <Table
              highlightOnHover
              withTableBorder
              withColumnBorders={false}
              styles={{
                table: {
                  borderRadius: '0 0 8px 8px',
                  overflow: 'hidden',
                  background: 'white',
                  borderTop: 'none',
                },
                th: { fontSize: 12, fontWeight: 600 },
              }}
            >
              {tableHead}
              <Table.Tbody>
                {sortCoaches(allCoaches).map(coach => renderCoachRow(coach))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      ) : (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No compensation calculations available yet.
        </Text>
      )}
    </Stack>
  );
}
